// Second-touch drafts in the CRM (SECOND-TOUCH-2026-10-02 §4) -- the data layer.
//
// pt-agents' second_touch job writes agent_drafts rows once a day. Here Matt reads them,
// edits them (re-checked on save with the SAME draft check), and sends them:
//
//   SMS    "Approve & send" -> the recruiter agent's existing authenticated staff path
//          (/api/staff-reply -> worker /staff-reply), on the thread that carried the
//          first text, so it leaves from the business line the agent texts from. The
//          lead is re-read first: consent must be true and opt-out false, or nothing
//          is sent. Inside the lead's quiet hours nothing is sent either: the draft is
//          marked 'approved' and HELD, the time the window opens is shown, and Matt
//          sends it from the same button once it is open. The worker's SID is recorded
//          as sent_message_id.
//   Email  "Approve" marks the draft approved and (EMAIL-CRM-2026-10-22) SENDS it from Matt through crm-mailer -- no
//          mail app any more; the outcome is on the timeline and the draft is marked sent. Before: a mailto: with the subject and
//          body prefilled, so the message leaves from Matt's own mail client;
//          "Mark sent" records that he sent it.
//   Either "Reject" with a reason (required, 500 characters at most).
//
// No bulk approve. A draft that failed the check, expired, or was rejected is never
// sendable. Every function takes the supabase client (and the send function) as
// arguments, so the tests run against fakes and the send path is never reached.
import { checkDraft } from './draftCheck';
import { resolveTimezone, withinDraftSendWindow, nextDraftSendTime } from './quietHours';
import { effectiveStatus } from './onboarding';

export const DRAFTS_TABLE = 'agent_drafts';
export const DRAFT_COLS =
  'id,lead_id,candidate_id,channel,body,subject,status,created_at,approved_at,sent_at,sent_message_id,rejected_reason,expires_at,check_passed,conversation_id,'
  + 'reminder_document_id,reminder_expires_on,reminder_window';   // the reminder columns: migrations/2026-10-10
export const LEAD_COLS = 'id,name,email,phone,state,discipline,form_type,sms_consent,sms_opt_out,spam_marked_at,processed';
// An expired draft Matt asks to re-draft is marked rejected with this reason; pt-agents' next daily run re-drafts
// that row in place if the lead still qualifies (agent_drafts keeps one row per lead: unique (lead_id)).
export const REDRAFT_REASON = 'expired';
export const REASON_MAX = 500;
export const BODY_MAX = 1600;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** True when a re-draft request (rejected for expiry) already has a newer row for its lead -- the re-draft
 *  written as a NEW row once agent_drafts allows one live row per lead (ruling 1b, 2026-10-02). */
export function isSuperseded(d, all) {
  if (!d || !all || d.status !== 'rejected' || d.rejected_reason !== REDRAFT_REASON) return false;
  return all.some((x) => x.id !== d.id && x.lead_id === d.lead_id && String(x.created_at || '') > String(d.created_at || ''));
}

/** The newest draft per lead (a lead may carry history rows beside its one live row). */
export function latestByLead(drafts) {
  const out = {};
  for (const d of drafts || []) {
    const cur = out[d.lead_id];
    if (!cur || String(d.created_at || '') > String(cur.created_at || '')) out[d.lead_id] = d;
  }
  return out;
}

/** awaiting | failed_check | held | sent | rejected | expired | redraft_requested | redrafted.
 *  Pass the whole list as `all` to tell an answered re-draft request (redrafted) from an open one. */
export function draftState(d, nowMs = Date.now(), all = null) {
  if (!d) return 'expired';
  if (d.status === 'sent') return 'sent';
  if (isSuperseded(d, all)) return 'redrafted';
  if (d.status === 'rejected') return d.rejected_reason === REDRAFT_REASON ? 'redraft_requested' : 'rejected';
  if (d.status === 'expired' || (d.expires_at && Date.parse(d.expires_at) <= nowMs)) return 'expired';
  if (d.status === 'approved') return 'held';
  return d.check_passed === true ? 'awaiting' : 'failed_check';
}

// DAY-DIRECTSTAFF-2026-10-22 follow-up: rejected reasons the agents write, in plain words; anything else is shown as written.
// 'text_consent': pt-agents expiry_reminder (main f59c3d6) drafts an email only when the candidate has no text consent, and
// rejects a still-open email draft for someone we text.
export const REJECTED_REASON_LABELS = { text_consent: 'they text with us, so no email' };
export const rejectedText = (reason) => REJECTED_REASON_LABELS[reason] || reason;

export const STATE_LABELS = {
  awaiting: 'Awaiting approval', failed_check: 'Failed the check', held: 'Approved, not yet sent',
  sent: 'Sent', rejected: 'Rejected', expired: 'Expired — re-draft?', redraft_requested: 'Re-draft requested', redrafted: 'Re-drafted',
};

export function draftError(code) {
  const e = new Error(code);
  e.code = code;
  return e;
}

/** Why this draft cannot be sent right now (empty = it can). Pure. */
export function sendBlockers(draft, lead, nowMs = Date.now()) {
  const out = [];
  const state = draftState(draft, nowMs);
  if (!['awaiting', 'held'].includes(state)) out.push(`draft_${state}`);
  if (!lead) return [...out, 'lead_missing'];
  if (checkDraft(draft.body, draft.channel, draft.subject, undefined, lead.discipline).length) out.push('check_failed');
  if (lead.spam_marked_at) out.push('lead_spam');
  if (draft.channel === 'sms') {
    if (!draft.conversation_id) out.push('no_thread');
    if (lead.sms_consent !== true) out.push('no_consent');
    if (lead.sms_opt_out === true) out.push('opted_out');
  } else if (!EMAIL_RE.test(String(lead.email || '').trim())) {
    out.push('no_email');
  }
  return out;
}

export const BLOCKER_LABELS = {
  draft_failed_check: 'it failed the draft check -- edit it first',
  draft_expired: 'it expired', draft_rejected: 'it was rejected', draft_sent: 'it was already sent',
  check_failed: 'it failed the draft check -- edit it first', lead_missing: 'the lead could not be read',
  lead_spam: 'the lead is marked spam', no_thread: 'there is no text thread to send on',
  no_consent: 'the lead has not consented to texts', opted_out: 'the lead replied STOP',
  no_email: 'there is no email address on file',
  not_expired: 'only an expired draft can be re-drafted', lead_handled: 'the lead is marked handled',
  candidate_missing: 'the candidate could not be read',
  link_revoked: 'the onboarding link was revoked', link_expired: 'the onboarding link has expired',
  onboarding_complete: 'onboarding is already complete', link_missing: 'the candidate has no onboarding link',
  link_unknown: "the onboarding link's status could not be read (allowlisted staff only)",
  nudge_automatic: 'the next reminder is drafted automatically while the checklist is still open',
  document_replaced: 'the document was replaced or its expiry date changed since this reminder was drafted',
  document_unknown: "the document's status could not be read (allowlisted staff only)",
  reminder_automatic: 'reminders are drafted automatically, sixty and fourteen days before the expiry',
};

// ---- Source and expiry (OVERNIGHT-AGENTS-MORE-2026-10-04 §6) ------------------------------------------------------
// Which writer made a draft: agent_drafts.source when the row carries it (pt-agents db/2026-10-05-drafts-source.sql,
// not yet applied), else INFERRED the way the morning brief's block D2 does -- an earlier row for the same lead the copy
// audit rejected -> an audit re-draft; an inbound on the draft's thread from before it was written -> a window draft
// (second touch never drafts a lead who replied); anything else -> second touch.
export const SOURCE_LABELS = { second_touch: 'Second touch', audit_redraft: 'Audit re-draft', window: 'Window', onboarding_nudge: 'Onboarding nudge',
  expiry_reminder: 'Expiry reminder' };
export const EXPIRY_SOON_HOURS = 48;
const AUDIT_REASON = (r) => { const s = String(r || ''); return s.startsWith('audit:') || s === 'missing_state_question'; };
const INBOUND_ROLES = ['candidate', 'user'];

/** draft id -> 'second_touch' | 'audit_redraft' | 'window'. inboundAt: conversation id -> [inbound times, ms]. */
export function draftSources(drafts, inboundAt = {}) {
  const out = {};
  for (const d of drafts || []) {
    if (SOURCE_LABELS[d.source]) { out[d.id] = d.source; continue; }
    const at = Date.parse(d.created_at);
    const audit = (drafts || []).some((y) => y.id !== d.id && y.lead_id === d.lead_id && y.status === 'rejected'
      && AUDIT_REASON(y.rejected_reason) && Date.parse(y.created_at) <= at);
    const replied = !audit && (inboundAt[d.conversation_id] || []).some((t) => t < at);
    out[d.id] = audit ? 'audit_redraft' : replied ? 'window' : 'second_touch';
  }
  return out;
}

/** A live draft (draft or approved, never sent, before its expiry): hours to expiry and whether it is within 48 h. */
export function expiryOf(d, nowMs = Date.now()) {
  const exp = Date.parse(d.expires_at);
  if (!Number.isFinite(exp)) return { hours: null, soon: false, live: false };
  const live = (d.status === 'draft' || d.status === 'approved') && !d.sent_at && exp > nowMs;
  const hours = (exp - nowMs) / 3600000;
  return { hours, soon: live && hours <= EXPIRY_SOON_HOURS, live };
}

/** The Drafts tab's order: live drafts expiring within 48 h first, soonest first; then the rest as given (newest first). */
export function sortForList(rows, nowMs = Date.now()) {
  const soon = rows.filter((d) => expiryOf(d, nowMs).soon).sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at));
  return [...soon, ...rows.filter((d) => !soon.includes(d))];
}

async function inboundTimes(client, cids) {
  if (!cids.length) return {};
  let data, error;
  try {
    ({ data, error } = await client.from('agent_messages').select('conversation_id,created_at')
      .in('conversation_id', cids).in('role', INBOUND_ROLES));
  } catch (e) { return null; }           // the Drafts tab still loads; these rows just show no source
  if (error) return null;
  const out = {};
  for (const m of data || []) (out[m.conversation_id] = out[m.conversation_id] || []).push(Date.parse(m.created_at));
  return out;
}

export async function loadDrafts(client) {
  // agent_drafts.source is applied (hub, 2026-10-05): read with the rest, no missing-column fallback -- an error is
  // an error, and App shows the Drafts tab's load failure as it does for any other.
  const { data, error } = await client.from(DRAFTS_TABLE).select(`${DRAFT_COLS},source`).order('created_at', { ascending: false });
  if (error) throw error;
  const drafts = data || [];
  // The source of every row; only the rows without a column value need their thread's inbound times (one read). A
  // failed read never blanks the tab: those rows show no source.
  const need = [...new Set(drafts.filter((d) => !SOURCE_LABELS[d.source] && d.conversation_id).map((d) => d.conversation_id))];
  const inbound = await inboundTimes(client, need);
  const src = draftSources(drafts, inbound || {});
  return drafts.map((d) => ({ ...d, _source: inbound === null && !SOURCE_LABELS[d.source] ? null : src[d.id] }));
}

export async function readLead(client, leadId) {
  const { data, error } = await client.from('leads').select(LEAD_COLS).eq('id', leadId).maybeSingle();
  if (error) throw error;
  return data || null;
}

/** Conditional update: only rows still in one of `fromStatuses` change. Returns the row. */
async function transition(client, draft, fromStatuses, patch) {
  const { data, error } = await client.from(DRAFTS_TABLE).update(patch)
    .eq('id', draft.id).in('status', fromStatuses).select(DRAFT_COLS);
  if (error) throw error;
  if (!data || data.length !== 1) throw draftError('changed_elsewhere');
  return data[0];
}

/** Save an edit. Re-checks; check_passed follows the result. Only a 'draft' can be edited. */
export async function saveEdit(client, draft, { body, subject, discipline = null }) {
  const b = String(body || '');
  if (!b.trim()) throw draftError('body_required');
  if (b.length > BODY_MAX) throw draftError('body_too_long');
  const subj = draft.channel === 'email' ? String(subject || '') : null;
  const fails = checkDraft(b, draft.channel, subj, undefined, discipline);
  const row = await transition(client, draft, ['draft'], { body: b, subject: subj, check_passed: fails.length === 0 });
  return { row, fails };
}

export async function rejectDraft(client, draft, reason) {
  const r = String(reason || '').trim();
  if (!r) throw draftError('reason_required');
  if (r.length > REASON_MAX) throw draftError('reason_too_long');
  return transition(client, draft, ['draft', 'approved'], { status: 'rejected', rejected_reason: r });
}

/**
 * "Approve & send" for an SMS. Re-reads the lead, refuses on any blocker, holds inside
 * quiet hours, otherwise sends through `sendReply({conversation_id, body})` (the CRM's
 * staff.reply) and records the SID. Returns {sent, sid} or {held, sendAt}.
 *
 * A lead with no state (or one that maps to no zone) is held to the strictest window
 * (withinDraftSendWindow: 09:00 Pacific - 20:00 Eastern), and the text is sent only when the
 * caller passes confirmUnknownState: true -- Matt has confirmed it (DINNER-AGENTS-2026-10-02 §3).
 * Holding for the window needs no confirmation: it sends nothing.
 */
export async function approveAndSendSms({ client, draft, sendReply, now = () => new Date(), confirmUnknownState = false }) {
  if (draft.channel !== 'sms') throw draftError('not_sms');
  const lead = await readLead(client, draft.lead_id);
  const nowMs = now().getTime();
  const blockers = sendBlockers(draft, lead, nowMs);
  if (blockers.length) throw Object.assign(draftError('blocked'), { blockers });
  if (!withinDraftSendWindow(nowMs, lead.state)) {
    const row = draft.status === 'approved' ? draft
      : await transition(client, draft, ['draft'], { status: 'approved', approved_at: new Date(nowMs).toISOString() });
    return { held: true, sendAt: nextDraftSendTime(nowMs, lead.state), row };
  }
  if (!resolveTimezone(lead.state) && confirmUnknownState !== true) throw draftError('state_unknown_unconfirmed');
  const res = await sendReply({ conversation_id: draft.conversation_id, body: draft.body });
  if (!res || !res.sid) throw draftError('no_sid');
  const at = new Date(nowMs).toISOString();
  const row = await transition(client, draft, ['draft', 'approved'], {
    status: 'sent', approved_at: draft.approved_at || at, sent_at: at, sent_message_id: res.sid,
  });
  return { sent: true, sid: res.sid, row };
}

/** Why an expired draft cannot be re-drafted now (empty = it can be requested). The daily run re-checks
 *  everything else the selection rule needs (a reply since, the 24 h age, the form type). */
export function redraftBlockers(draft, lead, nowMs = Date.now()) {
  if (isNudge(draft)) return ['nudge_automatic'];
  if (isReminder(draft)) return ['reminder_automatic'];
  const out = [];
  if (draftState(draft, nowMs) !== 'expired' || draft.status === 'sent') out.push('not_expired');
  if (!lead) return [...out, 'lead_missing'];
  if (lead.processed === true) out.push('lead_handled');
  if (lead.spam_marked_at) out.push('lead_spam');
  if (lead.sms_opt_out === true) out.push('opted_out');
  return out;
}

/** "Re-draft" on an expired draft: marks it rejected with REDRAFT_REASON for the next daily run. Sends nothing. */
export async function requestRedraft({ client, draft, now = () => new Date() }) {
  const lead = await readLead(client, draft.lead_id);
  const blockers = redraftBlockers(draft, lead, now().getTime());
  if (blockers.length) throw Object.assign(draftError('blocked'), { blockers });
  return transition(client, draft, ['draft', 'approved', 'expired'], { status: 'rejected', rejected_reason: REDRAFT_REASON });
}

// EMAIL-CRM-2026-10-22: the email an approved draft sends (crm-mailer, kind 'message'). The mailer appends Matt's
// signature block itself, so a draft's own sign-off -- the trailing block after the last blank line that names Matt or
// ProTherapy Staffing (draftCheck.emailParts) -- is taken off, or it would appear twice. The words above it go as written.
const SIGN_OFF = /\bMatt(?:hew)?\b|ProTherapy Staffing/i;
export function withoutSignOff(body) {
  const t = String(body || '').trim();
  const i = t.lastIndexOf('\n\n');
  if (i < 0) return t;
  const tail = t.slice(i + 2);
  return SIGN_OFF.test(tail) && tail.split('\n').length <= 6 ? t.slice(0, i).trim() : t;
}
const draftEmail = (to, draft, who) => ({ to: String(to || '').trim(), subject: String(draft.subject || '').trim(), body: withoutSignOff(draft.body), ...who });

// ---- Onboarding nudges (hub 2026-10-08) ----------------------------------------------------------------------------
// pt-agents' onboarding_nudge job drafts a reminder EMAIL for a candidate whose onboarding link went out 48 h+ ago and
// is not finished: an agent_drafts row with source onboarding_nudge and candidate_id, no lead
// (migrations/2026-10-08-agent-drafts-candidate.sql). It is approved like any email draft, except that it goes to the
// CANDIDATE's email, and Approve (and "Open in mail again") first re-reads the candidate's onboarding link: a link that
// is revoked, expired or complete -- or one this login cannot read (the allowlist) -- refuses. The token is never read.
export const NUDGE_SOURCE = 'onboarding_nudge';
export const isNudge = (d) => !!d && d.source === NUDGE_SOURCE && d.candidate_id !== null && d.candidate_id !== undefined;
const CANDIDATE_COLS = 'id,name,email';
const LINK_COLS = 'id,status,created_at,expires_at,completed_at,revoked_at';   // never the token
const LINK_BLOCKERS = { revoked: 'link_revoked', expired: 'link_expired', complete: 'onboarding_complete', none: 'link_missing' };

export async function readCandidate(client, id) {
  const { data, error } = await client.from('candidates').select(CANDIDATE_COLS).eq('id', id).maybeSingle();
  if (error) throw error;
  return data || null;
}

/** 'open' | 'revoked' | 'expired' | 'complete' | 'none' | 'unknown' -- the candidate's newest onboarding link. */
export async function readLinkState(client, candidateId, nowMs = Date.now()) {
  let data, error;
  try {
    ({ data, error } = await client.from('onboarding_sessions').select(LINK_COLS).eq('candidate_id', candidateId)
      .order('created_at', { ascending: false }).limit(1));
  } catch (e) { return 'unknown'; }
  if (error) return 'unknown';
  const s = (data || [])[0];
  if (!s) return 'none';
  const st = effectiveStatus(s, nowMs);
  return ['pending', 'in_progress'].includes(st) ? 'open' : st;
}

/** Why this nudge cannot be approved now (empty = it can). link null = not read yet (the panel; Approve reads it). */
export function nudgeBlockers(draft, candidate, link, nowMs = Date.now()) {
  const out = [];
  const state = draftState(draft, nowMs);
  if (!['awaiting', 'held'].includes(state)) out.push(`draft_${state}`);
  if (!candidate) return [...out, 'candidate_missing'];
  if (checkDraft(draft.body, 'email', draft.subject).length) out.push('check_failed');
  if (!EMAIL_RE.test(String(candidate.email || '').trim())) out.push('no_email');
  if (link !== null && link !== 'open') out.push(LINK_BLOCKERS[link] || 'link_unknown');
  return out;
}

/** "Approve" for an email: marks it approved and returns the email to send ({ to, subject, body, leadId | candidateId }). */
export async function approveEmail({ client, draft, now = () => new Date() }) {
  if (draft.channel !== 'email') throw draftError('not_email');
  if (isNudge(draft)) return approveNudge({ client, draft, nowMs: now().getTime() });
  if (isReminder(draft)) return approveReminder({ client, draft, nowMs: now().getTime() });
  const lead = await readLead(client, draft.lead_id);
  const nowMs = now().getTime();
  const blockers = sendBlockers(draft, lead, nowMs);
  if (blockers.length) throw Object.assign(draftError('blocked'), { blockers });
  const row = draft.status === 'approved' ? draft
    : await transition(client, draft, ['draft'], { status: 'approved', approved_at: new Date(nowMs).toISOString() });
  return { email: draftEmail(lead.email, draft, { leadId: lead.id || draft.lead_id }), row };
}

async function approveNudge({ client, draft, nowMs }) {
  const candidate = await readCandidate(client, draft.candidate_id);
  const link = await readLinkState(client, draft.candidate_id, nowMs);
  const blockers = nudgeBlockers(draft, candidate, link, nowMs);
  if (blockers.length) throw Object.assign(draftError('blocked'), { blockers });
  const row = draft.status === 'approved' ? draft
    : await transition(client, draft, ['draft'], { status: 'approved', approved_at: new Date(nowMs).toISOString() });
  return { email: draftEmail(candidate.email, draft, { candidateId: candidate.id }), row };
}

// ---- Expiry reminders (OVERNIGHT-DIRECTSTAFF-2026-10-10 §3) ----------------------------------------------------------
// pt-agents' expiry_reminder job drafts a reminder EMAIL sixty and fourteen days before a candidate's license or CPR
// card expires: an agent_drafts row with source expiry_reminder, candidate_id and the document it is about
// (migrations/2026-10-10-agent-drafts-expiry-reminder.sql). Hub ruling 2026-10-10: NO link and no session read -- the
// email asks the candidate to reply with a photo or PDF of the renewal, and staff record it with "Record renewal" on the
// Expirations row. Approve (and "Open in mail again") re-reads the candidate and the document, refuses when the document
// was replaced or re-dated since the draft, and sends the body as stored (its sign-off left to the mailer's signature).
export const REMINDER_SOURCE = 'expiry_reminder';
export const isReminder = (d) => !!d && d.source === REMINDER_SOURCE && d.candidate_id !== null && d.candidate_id !== undefined;
/** A draft that belongs to a candidate (a nudge or an expiry reminder), not a lead. */
export const isCandidateDraft = (d) => isNudge(d) || isReminder(d);

/** 'current' | 'replaced' | 'unknown': is the reminder's document still the candidate's current one, with that date? */
export async function readReminderDocument(client, draft) {
  let data, error;
  try {
    ({ data, error } = await client.from('onboarding_expirations').select('document_id,expires_on')
      .eq('candidate_id', draft.candidate_id));
  } catch (e) { return 'unknown'; }
  if (error) return 'unknown';
  const cur = (data || []).find((r) => String(r.document_id) === String(draft.reminder_document_id));
  return cur && String(cur.expires_on) === String(draft.reminder_expires_on) ? 'current' : 'replaced';
}

/** Why this reminder cannot be approved now (empty = it can). doc null = not read yet (the panel; Approve reads it). */
export function reminderBlockers(draft, candidate, doc, nowMs = Date.now()) {
  const out = [];
  const state = draftState(draft, nowMs);
  if (!['awaiting', 'held'].includes(state)) out.push(`draft_${state}`);
  if (!candidate) return [...out, 'candidate_missing'];
  if (checkDraft(draft.body, 'email', draft.subject).length) out.push('check_failed');
  if (!EMAIL_RE.test(String(candidate.email || '').trim())) out.push('no_email');
  if (doc === 'replaced') out.push('document_replaced');
  else if (doc === 'unknown') out.push('document_unknown');
  return out;
}

async function approveReminder({ client, draft, nowMs }) {
  const candidate = await readCandidate(client, draft.candidate_id);
  const doc = await readReminderDocument(client, draft);
  const blockers = reminderBlockers(draft, candidate, doc, nowMs);
  if (blockers.length) throw Object.assign(draftError('blocked'), { blockers });
  const row = draft.status === 'approved' ? draft
    : await transition(client, draft, ['draft'], { status: 'approved', approved_at: new Date(nowMs).toISOString() });
  return { email: draftEmail(candidate.email, draft, { candidateId: candidate.id }), row };
}

/** "Mark sent": set automatically once crm-mailer accepts the email; by hand for one Matt sent some other way. */
export async function markEmailSent({ client, draft, now = () => new Date() }) {
  if (draft.channel !== 'email') throw draftError('not_email');
  if (draft.status !== 'approved') throw draftError('not_approved');
  return transition(client, draft, ['approved'], { status: 'sent', sent_at: now().toISOString() });
}

/** ?draft=<uuid> deep link (the brief's block D links here). */
export function parseDraftParam(search) {
  const v = new URLSearchParams(search || '').get('draft');
  return v && /^[0-9a-f-]{36}$/i.test(v) ? v.toLowerCase() : null;
}

export function withoutDraftParam(search) {
  const params = new URLSearchParams(String(search || ''));
  params.delete('draft');
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function awaitingCount(drafts, nowMs = Date.now()) {
  return (drafts || []).filter((d) => draftState(d, nowMs) === 'awaiting').length;
}
